import {
  Box,
  Button,
  Card,
  FormControl,
  FormGroup,
  Grid,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { SubmitHandler, useForm } from "react-hook-form";
import { asPercentage } from "components/ui/formatters";
import { MaxDropLimits } from "helpers/types";
import { drawdownComparisonEditModalStyles } from "../../../../styles/DrawdownComparisonChart.styles";

interface Props {
  isOpened: boolean;
  onClose: () => void;
  maxDrop: number;
  negativeLimit: number;
  onLimitsChange: (limits: MaxDropLimits) => void;
}

export const DrawdownComparisonEditModal = ({
  isOpened,
  onClose,
  maxDrop,
  negativeLimit,
  onLimitsChange,
}: Props) => {
  const { register, handleSubmit, reset } = useForm<MaxDropLimits>({
    defaultValues: { maxDrop, negativeLimit },
  });

  const handleFormSubmit: SubmitHandler<MaxDropLimits> = (data) => {
    onLimitsChange(data);
    onClose();
  };

  // Seeded on open, not on close: Modal unmounts only its inner subtree, so
  // the form itself survives and would otherwise keep the last typed values.
  useEffect(() => {
    if (!isOpened) return;

    reset({ maxDrop, negativeLimit });
  }, [isOpened, maxDrop, negativeLimit, reset]);

  return (
    <Modal open={isOpened} onClose={onClose}>
      <Stack height="100%" alignItems="center" justifyContent="center">
        <Box
          sx={drawdownComparisonEditModalStyles.modalContainer}
          component="form"
          onSubmit={handleSubmit(handleFormSubmit)}
        >
          <Card>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item>
                <Typography variant="h5" component="h5" sx={{ padding: 1 }}>
                  Edit Drawdown Limit
                </Typography>
              </Grid>
              <Grid item>
                <IconButton onClick={onClose} aria-label="Close modal">
                  <CloseIcon />
                </IconButton>
              </Grid>
            </Grid>
            <FormGroup>
              <FormControl sx={{ margin: 1 }}>
                <TextField
                  label="Maximum Drop (%)"
                  type="number"
                  inputProps={{ step: "1", min: "1" }}
                  {...register("maxDrop", {
                    required: true,
                    valueAsNumber: true,
                    min: {
                      value: 1,
                      message: "Maximum drop must be at least 1%",
                    },
                  })}
                  helperText={`Current maximum drop: ${asPercentage(maxDrop)}`}
                />
              </FormControl>
              <FormControl sx={{ margin: 1 }}>
                <TextField
                  label="Negative Limit (%)"
                  type="number"
                  inputProps={{ step: "1", max: "-1" }}
                  {...register("negativeLimit", {
                    required: true,
                    valueAsNumber: true,
                    max: {
                      value: -1,
                      message:
                        "Negative limit must be less than or equal to -1%",
                    },
                  })}
                  helperText={`Current negative limit: ${asPercentage(negativeLimit)}`}
                />
              </FormControl>
              <Box sx={drawdownComparisonEditModalStyles.buttonsContainer}>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={onClose}
                  sx={drawdownComparisonEditModalStyles.button}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={drawdownComparisonEditModalStyles.button}
                >
                  Save
                </Button>
              </Box>
            </FormGroup>
          </Card>
        </Box>
      </Stack>
    </Modal>
  );
};
